"use client";

import { useState } from "react";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getPatientNotes,
  createPatientNote,
  updatePatientNote,
  deletePatientNote,
} from "../../services/patient/settings";
import Table from "../table";
import Modal from "../shared-modules/Modal";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import Spinner from "../spinner";
import Reload from "../ui/reload-data";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "../button-v1";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const schemaValidation = z.object({
  note_type: z.string().min(1, "Note type is required"),
  note_text: z.string().min(1, "Note text is required"),
});

const defaultSchema = {
  note_type: "",
  note_text: "",
};

const Notes = ({ isOpen, toggleSection }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schemaValidation),
    defaultValues: defaultSchema,
  });

  const {
    data: patientNotes,
    isLoading,
    error,
    refetch,
  } = useQuery(["patientNotes", patientId], () => getPatientNotes(patientId), {
    enabled: !!patientId,
    onError: (err) => {
      toast.error("Failed to load patient notes. Please try again.");
    },
    select: (data) => {
      return data.filter(
        (note) =>
          note.note_type === "quick_note" || note.note_type === "sticky_note"
      );
    },
    staleTime: 2 * 60 * 1000,
  });

  const note_type = watch("note_type");

  const quickNoteExists = () => {
    if (!patientNotes) return false;
    return patientNotes.some((note) => note.note_type === "quick_note");
  };

  const getExistingQuickNote = () => {
    if (!patientNotes) return null;
    return patientNotes.find((note) => note.note_type === "quick_note");
  };

  const stickyNotesLimitReached = () => {
    if (!patientNotes) return false;
    const stickyNotes = patientNotes.filter(
      (note) => note.note_type === "sticky_note"
    );
    return stickyNotes.length >= 1;
  };

  const getStickyNotesCount = () => {
    if (!patientNotes) return 0;
    return patientNotes.filter((note) => note.note_type === "sticky_note")
      .length;
  };

  const createNoteMutation = useMutation(
    (data) => createPatientNote(data, patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patientNotes", patientId]);
        setIsModalOpen(false);
        reset(defaultSchema);
        toast.success("Note added to patient successfully");
      },
      onError: (error) => {
        console.error("Could not create note:", error);
        toast.error("Failed to add note. Please try again.");
      },
    }
  );

  const updateNoteMutation = useMutation(
    ({ id, ...data }) => updatePatientNote(id, data, patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patientNotes", patientId]);
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentNote(null);
        reset(defaultSchema);
        toast.success("Patient note updated successfully");
      },
      onError: (error) => {
        console.error("Could not update note:", error);
        toast.error("Failed to update note. Please try again.");
      },
    }
  );

  const deleteNoteMutation = useMutation(
    (noteId) => deletePatientNote(noteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patientNotes", patientId]);
        setDeleteConfirmOpen(false);
        setNoteToDelete(null);
        toast.success("Note Deleted successfully");
      },
      onError: (error) => {
        console.error("Could not delete note:", error);
        toast.error("Failed to remove note. Please try again.");
        setDeleteConfirmOpen(false);
        setNoteToDelete(null);
      },
    }
  );

  const handleAddNote = (data) => {
    if (!isEditing && data.note_type === "quick_note" && quickNoteExists()) {
      const existingQuickNote = getExistingQuickNote();
      if (existingQuickNote) {
        updateNoteMutation.mutate({
          id: existingQuickNote.id,
          note_text: data.note_text,
        });
        return;
      }
    }

    if (
      !isEditing &&
      data.note_type === "sticky_note" &&
      stickyNotesLimitReached()
    ) {
      toast.error("You can only have 1 Sticky Note per patient");
      return;
    }

    if (isEditing && currentNote) {
      updateNoteMutation.mutate({
        id: currentNote.id,
        note_text: data.note_text,
      });
    } else {
      createNoteMutation.mutate({
        note_text: data.note_text,
        note_type: data.note_type,
      });
    }
  };

  const handleEdit = (row) => {
    setCurrentNote(row);
    setIsEditing(true);
    setValue("note_type", row.note_type);
    setValue("note_text", row.note_text);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    if (row.id) {
      setNoteToDelete(row);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = () => {
    if (noteToDelete && noteToDelete.id) {
      deleteNoteMutation.mutate(noteToDelete.id);
    }
  };

  const handleOpenModal = () => {
    setIsEditing(false);
    reset(defaultSchema);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentNote(null);
    reset(defaultSchema);
  };

  const getNoteTypeName = (noteType) => {
    switch (noteType) {
      case "quick_note":
        return "Quick Note";
      case "sticky_note":
        return "Sticky Note";
      case "rpm_note":
        return "RPM Note";
      case "ccm_note":
        return "CCM Note";
      default:
        return noteType;
    }
  };

  const columns = [
    {
      key: "note_type",
      label: "Note Type",
      alignLeft: true,
      render: (row) => getNoteTypeName(row.note_type),
    },
    {
      key: "note_text",
      label: "Description",
      alignLeft: true,
      sorting: false,
      render: (row) => row.note_text,
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (row) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
            disabled={deleteNoteMutation.isLoading}
          >
            <FaEdit color="black" />
          </Button>{" "}
          {deleteNoteMutation.isLoading && noteToDelete?.id === row.id ? (
            <Spinner size="small" />
          ) : (
            <Button
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
              disabled={deleteNoteMutation.isLoading}
            >
              <FaTrash color="black" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const allNotesLimitReached = quickNoteExists() && stickyNotesLimitReached();

  const modalFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
      <Button
        onClick={handleSubmit(handleAddNote)}
        loading={createNoteMutation.isLoading || updateNoteMutation.isLoading}
        disabled={createNoteMutation.isLoading || updateNoteMutation.isLoading}
      >
        {isEditing ? "Update" : "Save"}
      </Button>
    </div>
  );

  const deleteModalFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
      <Button
        onClick={confirmDelete}
        loading={deleteNoteMutation.isLoading}
        disabled={deleteNoteMutation.isLoading}
      >
        Delete
      </Button>
    </div>
  );

  return (
    <div className={`${styles.form_section} ${isOpen ? "" : styles.close}`}>
      <div
        className={styles.section_header}
        onClick={() => toggleSection("sec5")}
      >
        <div className={styles.section_number}>5</div>
        <div>
          <p className={styles.section_heading}>Notes</p>
          <p className={styles.sub_heading}>Patient Notes</p>
        </div>
        <div className={styles.drop_icon}>
          <FontAwesomeIcon icon={faChevronDown} />
        </div>
      </div>

      <div className={`${styles.input_fields} ${styles.status_main_cont}`}>
        <div className={styles.statuses_cont}>
          <p className={styles.section_heading}>Notes</p>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button onClick={handleOpenModal} disabled={allNotesLimitReached}>
              <FaPlus size="10px" /> Add Note
            </Button>
          </div>
        </div>

        <div className={styles.status_cont}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <Spinner />
            </div>
          ) : error ? (
            <Reload onReload={refetch} message="Could not load patient notes" />
          ) : (
            <Table
              columns={columns}
              data={patientNotes || []}
              loading={false}
              isRowClickable={false}
              height={400}
            />
          )}
        </div>
      </div>

      <Dialog open={isModalOpen} setOpen={handleCloseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Patient Note" : "Add Note to Patient"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form onSubmit={handleSubmit(handleAddNote)}>
              <section
                className={styles.input_fields}
                style={{ marginBottom: "0px", width: "100%" }}
              >
                <label>Note Type</label>
                <div className={styles.input_wrapper}>
                  <Select
                    onChange={(value) => setValue("note_type", value)}
                    value={note_type}
                    disabled={isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type">
                        {note_type ?? "Select code type"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {!isEditing && !quickNoteExists() && (
                        <SelectItem value="quick_note">Quick Note</SelectItem>
                      )}
                      {!isEditing && !stickyNotesLimitReached() && (
                        <SelectItem value="sticky_note">Sticky Note</SelectItem>
                      )}
                      {isEditing && (
                        <>
                          <SelectItem value="quick_note">Quick Note</SelectItem>
                          <SelectItem value="sticky_notes">
                            Sticky Note
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {errors.note_type && (
                    <p className="error-message">{errors.note_type.message}</p>
                  )}
                </div>

                <label>Description</label>
                <div className={styles.input_wrapper}>
                  <textarea
                    {...register("note_text")}
                    className={`${styles.input} ${
                      errors.note_text ? styles.error : ""
                    }`}
                    rows={4}
                    placeholder="Enter a description for this note"
                    maxLength={100}
                  />
                  <p>Max 100 characters are allowed</p>
                  {errors.note_text && (
                    <p className="error-message">{errors.note_text.message}</p>
                  )}
                </div>
              </section>
            </form>
          </DialogBody>
          <DialogFooter>{modalFooter}</DialogFooter>
        </DialogContent>
      </Dialog>

      <Modal
        title="Confirm Delete"
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        footer={deleteModalFooter}
        width={"500px"}
      >
        <div>
          <p>Are you sure you want to remove this note from the patient?</p>
          {noteToDelete && (
            <div style={{ marginTop: "10px" }}>
              <p>
                <strong>Note Type:</strong>{" "}
                {getNoteTypeName(noteToDelete.note_type)}
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Notes;
